/**
 * Quiz design tokens — single source of truth for the redesigned quiz (screens 1–12).
 *
 * Colours and font families resolve to the global `--pop-*` CSS variables that
 * `snippets/pop-components.liquid` + `snippets/pop-fonts.liquid` render in <head>
 * on every storefront page (incl. the quiz). Hex/stack fallbacks are inlined so the
 * values still render correctly inside Storybook / tests / any page missing the vars.
 *
 * Use these in MUI `sx` props instead of hardcoding hex values, e.g.
 *   sx={{ bgcolor: QUIZ.color.cream, fontFamily: QUIZ.font.display }}
 */
export const QUIZ = {
  color: {
    brown: "var(--pop-brown, #4a3026)",
    ink: "var(--pop-ink, #332115)",
    dark: "var(--pop-dark, #1c120e)",
    grey: "var(--pop-grey, #3e3e3e)",
    cream: "var(--pop-cream, #faf5eb)",
    cream2: "var(--pop-cream-2, #e9dfd7)",
    cream3: "var(--pop-cream-3, #d7c7b4)", // input borders
    blue: "var(--pop-blue, #8ee5ff)",
    green: "var(--pop-green, #cced85)",
    white: "#ffffff",
    warmWhite: "var(--pop-warm-white, #fffcf5)", // card/dialog surfaces on cream
    placeholder: "#b0b0b0",
    statLabel: "#9098a3", // muted grey on profile stat cards
    error: "#c62828",
    sage: "var(--pop-sage, #3B7A2A)",
    purple: "#E8C4FF",
    lavender: "#E4B8F0",
    coral: "#FF7874",
    red: "var(--pop-red, #ff625d)",
    trustpilotGreen: "#00b67a",

  },
  font: {
    display: "var(--pop-font-display, 'RL Aqva', 'Prompt', sans-serif)",
    body: "var(--pop-font-body, 'Archivo', sans-serif)",
    button: "var(--pop-font-button, 'Jost', 'Prompt', sans-serif)",
    label: "var(--pop-font-label, 'Kuillo Chunk', 'Prompt', sans-serif)",
  },
  radius: {
    input: "10px", // mobile inputs
    inputLg: "14px", // desktop inputs
    card: "17px", // profile stat cards
    pill: "100px",
  },
  /** Heights of the fixed chrome, exposed as CSS vars on the shell for layout padding. */
  layout: {
    navMobile: 81,
    navDesktop: 96,
    heroMobile: 194,
    bottomBarMobile: 70,
    bottomBarDesktop: 76,
  },
} as const;

export type QuizTokens = typeof QUIZ;
